/*
 * Copyright (c) 2026. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

import {Injectable} from '@angular/core';
import {SpinJS, Sihwlog, SihwcacheService} from 'sihw-ngx';
import {IndividualConfig, ToastrService} from 'ngx-toastr';
import {NgxSpinnerService} from 'ngx-spinner'; //https://www.npmjs.com/package/ngx-toastr
import {cloneDeep, sortBy} from 'lodash-es';
import {firstValueFrom, Observable, Subject} from 'rxjs';
import {filter} from 'rxjs/operators';
import {SihwconfirmService, LocalConfig} from 'sihw-ngx';
import {userdata, hash, inkooporder} from '../../app/types';
import {Basemodel} from '../../models/basemodel';
import {Project} from '../../models/project';

///////////////////////// INTERFACES ////////////////////////////////////////////////////////
export type Datarecord = hash<any>;
export type Datarecords = Datarecord[];

////Opties voor ophalen van records uit het backend
export interface Recordquery {
  table: string, //de tabelnaam van de hoofdtabel
  lookup?: string, //Tabelnaam van de tabel waarvan we de permissieruimte gebruiken. Deze tabel moet dan qua acl de juiste toestemmingen voor de lookup verlenen. Bijvoorbeeld: wij zoeken alle mogelijke bestemmingen omdat we aan het werk zijn met de tabel routes. De contexttabel bepaalt dan qua acl of dit mag.
  single?: string | Number, //als gegeven, dan bevat het één id. Het resultaat is dan één record, niet in een array
  hashkey?: string, //als dit gegeven is (en options.single niet), dan wordt er geen array teruggegeven, maar een hash-object met als keys de (string-)waarde van het hier opgegeven attribute, en als value het record
  attributes?: string[], //velden die moeten worden teruggegeven
  where?: hash<any>, //Object met filters op velden, filters volgen de sequelize where-syntax
  order?: (string | string[])[], //array met velden in sequelize order-syntax ("createdAt desc", of ['createdAt','desc'])
  connected?: ConnectedRecordquery[], //array met verbonden 1:n of 1:1 records uit andere tabellen. De verbindingen gaan alleen via foreign key-includes die sequelize kent.
}

//de definitie van een connected-query binnen een Recordquery: hetzelfde als de basis Recordquery, inclusief recursief connected. Maar met een paar extra opties:
export interface ConnectedRecordquery extends Recordquery {
  as?: string, // Als gegeven, dan is het een alias voor een foreign key-relatie die door spinjs2 is gegenereerd omdat er meerdere velden uit deze tabel naar één en dezelfde foreign table wijzen. Dan is het as-veld verplicht. Spinjs2 genereert de alias dan als <lokaleveldnaam> + '_data'. Bij een zelfverwijzing is het <lokaleveldnaam> + 's' Die namen moeten hier letterlijk gebruikt worden, en de data komt ook in een veld met die naam terecht.
  required?: boolean, //ls gegeven en true, dan wordt het hoofdrecord niet teruggegeven als er geen passend subrecord is. Zie sequelize include.required. Let op: default is anders dan false. Als fixednames niet gezet is in spinjs2, dan staan connected 1:n records in het record als tabelnaam met een s erachter ('contactpersoons'), 1:1 gewoon als tabelnaam. Met fixednames altijd alles als tabelnaam (beter).
  empty?: boolean //Als gegeven en true doen de andere velden in de connected er niet toen en worden er alleen records teruggegeven die GEEN verbonden records in de betreffende tabel hebben.
}

///////////////////////////////////////////////////////////////////////////////////////////////
const apiversion = 1; //wordt meegestuurd naar backend
const beErrors = {
  //mapping van backenderrors op meldingen. Zie send
  HANDLER_EXCEPTION: 'Er is een serverfout opgetreden',
  INTERNAL_ERROR: 'Er is een serverfout opgetreden',
  MISSING_DATA: 'Serverfout: ongeldige aanroep',
  NOT_APPLICABLE: 'Deze actie is niet toepasbaar',
  NOT_FOUND: 'Niet beschikbaar',
  NO_AUTH: 'Geen toestemming',
  OVER_QUOTA: 'Deze actie is tijdelijk niet beschikbaar omdat hij te vaak is uitgevoerd.',
  DOUBLE_DATA: 'Deze wijziging kan niet worden doorgevoerd, omdat de gegevens al bestaan.',
  USED_DATA: 'Deze wijziging kan niet worden doorgevoerd, omdat de gegevens elders gebruikt worden.'
};

@Injectable()
export class ApiService {
  private log: Sihwlog;

  private _besettings: any = {}; //door het backend meegestuurde settings
  private _userdata: userdata = null; //de userdata vanuit het backend
  private _werkmaatschappij: { id: number, naam: string, afkorting: string } = null;
  public performancedata = {
    load: 0
  };
  //intern voor blocker
  private blockcount: number = 0;
  private blockwait: any = null; //timeouthandle

  //promises voor processen
  private readonly _init: Promise<void> = null; //zijn we klaar met init?
  private _loggedInPromise: Promise<any> = null; //niet dubbel vragen aan be


  //subjects
  protected dataWijzigingSubject = new Subject<{ table: string | string[], delete: boolean, ids: number[], self: boolean }>();

  // Subjects/events
  // Doel: externen informeren over belangrijke lifecycle-events van de API (zoals logout)
  private readonly loggedOutSubject = new Subject<void>();
  /**
   * Doel: observable die een signaal afvuurt zodra logout is afgerond.
   */
  public readonly loggedOut$ = this.loggedOutSubject.asObservable();

  private conf = new LocalConfig();

  constructor(private toastr: ToastrService, private spinner: NgxSpinnerService, private spinJS2: SpinJS, private cache: SihwcacheService, private sihwConfirm: SihwconfirmService) {
    this.log = new Sihwlog('debug', 'Api');
    this.log.debug(`API geladen`);

    //handle spinjs-events
    this.spinJS2.onInternal(
      {
        next: async (v: { event: any; }) => {
          this.log.debug(`Spinjs2 event`, v);
          switch (v.event) {
            case 'connect':
              //haal de be-settings
              try {
                //clear de cache
                this.cache.clear();
                this.log.debug('*** Ophalen besettings');
                this._besettings = await this.send('auth', 'beSettings', {}, false, false);
                //herregistratie gaat op verzoek
              } catch (e) {
                this.log.error(e);
              }
          }
        }
      });

    this.spinJS2.onNotify(async v => {
      this.backendNotify(v);
    });

    //we runnen de rest van onze init async
    //en onthouden of we klaar zijn
    this._init = new Promise(async (resolve) => {
      //doe wat er moet gebeuren
      await this.spinJS2.whenConnected(); //eerst moet SpinJS er zijn
      //..... todo: overige init
      resolve(); //zet this._init op een resolve promise. Zie ready
    });
  }

  /**
   * Check of de api ready is. bijvoorbeeld: await this.api.ready;
   */
  public get ready(): Promise<void> {
    return this._init;
  }

  public get connected(): boolean {
    return this.spinJS2.connected;
  }

  /*  public async test(): Promise<void> {
      return this.send('test','pauze',{pauze: 1});
    }*/
  /**
   * Geeft een promise van een boolean om te kijken of de user in ingelogd
   * De promise wordt pas resolved als de api klaar in met initialiseren
   * @param force Als true dan relogin via backend, zelfs als we userdata hebben
   * @param onReconnect Geeft aan of we dit doen na een reconnect, we tonen dan geen toast
   */
  public async loggedIn(force: boolean = false, onReconnect: boolean = false): Promise<boolean> {
    await this.ready; //klaar met init?
    //test
    if (!this.spinJS2.hasAuth) {
      //gaat hem niet worden
      this.log.debug(`loggedIn: geen auth = niet ingelogd`);
      return false;
    }

    if (this._userdata && (!force)) {
      //dat ziet er goed genoeg uit
      return true;
    }

    //wel een auth, nog geen data. We zullen het moeten vragen
    let vanons = false;
    try {
      if (!this._loggedInPromise) {
        //zo combineren we meerdere calls. Dus maken we nu een promise
        vanons = true;
        this._loggedInPromise = this.send('auth', 'relogin', null, true, false); //geen error
      } else {
        this.log.debug(`loggedIn()-call gecombineerd met lopende`);
      }
      let res = await this._loggedInPromise; //resultaat, of dit nou onze promise is of niet
      if (vanons) {
        this._loggedInPromise = null; //weer resetten
      }
      this.log.debug(`loggedIn backend check geslaagd`, res);
      await this.afterLogin(res, onReconnect); //bewaar de data
      return true; //klaar
    } catch (e) {
      if (vanons) {
        this._loggedInPromise = null; //weg ermee
      }
      this.log.debug(e);
      await this.logout();
      return false;
    }
  }

  /**
   * Geef terug of we nu zijn ingelogd, zonder dit aan het be te vragen
   */
  public get loggedInSync(): boolean {
    return this.spinJS2.hasAuth && (!!this._userdata);
  }

  /**
   * Clear alle data
   */
  public async logout() {
    //backend melden, zodat daar client-zaken opgeruimd kunnen worden
    await this.send('auth', 'logout');
    this.spinJS2.authKey = null;
    this._userdata = null;
    //en weg met de cache
    this.cache.clear();
    // Meld aan luisteraars (zoals FiltertoolFactory) dat er een logout is geweest
    // Zodat zij eigen opruimacties (bijv. instanties leegmaken) kunnen doen
    try {
      this.loggedOutSubject.next();
    } catch (e) {
      // defensief: dit mag logout niet blokkeren
      this.log?.warn?.('Kon logout-event niet uitsturen', e);
    }
  }


  ////////////////////// info ///////////////////////////

  public get userdata(): userdata {
    return this._userdata ? Object.assign({}, this._userdata) : null;
  }

  public get uid(): number {
    return this._userdata ? this._userdata.uid : null;
  }

  /**
   * Geeft de info over de ingelogde werkmaatschappij terug
   */
  public get werkmaatschappij(): { id: number, naam: string, afkorting: string } {
    return this._werkmaatschappij;
  }

  /**
   * Geeft een beSetting-waarde terug, of een default
   * @param key
   * @param standaard
   */
  public beSetting(key: string, standaard?: any): any {
    return this._besettings[key] ?? standaard;
  }

  ////////////////// ACL ///////////////////////////
  /**
   * Implementatie van de can-functie in het backend, gegeven de acl in de userdata
   * @param table een table of een lijst van tables (dat is dan OF)
   * @param action een actie of een lijst van acties (dat is dan OF)
   * Als een argument een array is, dan moet 1 van de elementen slagen in combinatie met het andere argument
   * Een acl-lijst bestaat uit spatiegescheiden acl-sleutels (of een al gesplitte array, zoals in de userdata). Dit is <model|*>:actie. De zoekvolgorde in de can()-functie is als volgt.
   * can(model, actie: read|full|readonly|speciaal)
   *
   * 1. ALS de key <model>:none bestaat: faal
   * 2. Check de gevraagde key:
   *     'read': <model>:read of <model>:full moet bestaan
   *     'full': <model>:full moet bestaan
   *     'readonly': <model>:read moet bestaan EN <model>:full mag niet bestaan
   *     (speciale actie>: <model>:(speciale actie) moet bestaan
   *     '*': er moet een actie (niet 'none') bestaan
   * 3. ALLEEN ALS er geen enkele key is met specifiek het model, herhaal dan stap drie met '*' in plaats van de modelnaam
   *
   * Dus zodra er iets specifieks voor een model staat, geldt de * niet meer
   */
  public can(table: string | string[], action: string | string[]): boolean {
    if (!(this.loggedInSync && this._userdata)) {
      return false;
    }

    if (Array.isArray(table)) {
      return table.some(t => this.can(t, action));
    }

    if (Array.isArray(action)) {
      return action.some(a => this.can(table, a));
    }

    let acl = this._userdata.acl;

    if (acl.includes(`${table}:none`))
    {
      return false; //nope
    }

    //houdt dus geen rekening met specials in backendmodellen
    const specifiek: string[] = [];
    const algemeen: string[] = [];

    for(let key of acl)
    {
      const [m,a] = key.split(':');
      if (m === table)
      {
        specifiek.push(a);
      }
      else if (m === '*') {
        algemeen.push(a);
      }
    }

    //als er iets specifieks is, geldt de algemene niet
    const checkacties = specifiek.length ? specifiek : algemeen;

    switch (action) {
      case '*':
        return checkacties.length > 0;
      case 'read':
        return checkacties.includes('read') || checkacties.includes('full');
      case 'full':
        return checkacties.includes('full');
      case 'readonly': // wel lezen, niet schrijven
        return checkacties.includes('read') && (! checkacties.includes('full'));
      default:
        //speciale actie
        return checkacties.includes(action);
    }
  }

  /**
   * Return een observable voor datawijzigingen op specifieke tabellen of modellen (1 of meer)
   * @param models
   */
  public onDatawijziging(models: string | typeof Basemodel | (string | typeof Basemodel)[]): Observable<{
    table: string | string[],
    delete: boolean,
    ids: number[],
    self: boolean
  }> {
    if (!Array.isArray(models)) {
      models = [models]; //nu wel
    }
    let tables = models.map(m => (typeof (m) === 'string') ? m : m.table);
    return this.dataWijzigingSubject.pipe(
      filter(w => (Array.isArray(w.table) ? w.table : [w.table]).some(table => tables.includes(table))));
  }

  /**
   * Return een observable voor een wijziging van een specifieke id in een tabel. Dus niet algemeen (maar wel als er geen keys zijn)
   * @param table
   * @param id
   */
  public onRecordwijziging(table: string, id: any): Observable<{ table: string | string[], delete: boolean, ids: number[] }> {
    return this.dataWijzigingSubject.pipe(
      filter(w =>(Array.isArray(w.table) ? w.table : [w.table]).includes(table) && w.ids && w.ids.includes(id)));
  }

  /////////////////// specifieke be-calls
  /**
   * Logt in het backend in. throwt een errorcode als dat misgaat. Anders gewoon resolve
   * @param email
   * @param wachtwoord
   */
  public async login(email: string, wachtwoord: string): Promise<void> {
    await this.logout(); //uitloggen
    try {

      let res = await this.send('auth', 'login', {
        scope: 'gebruiker', //in deze applicatie loggen we gebruikers in
        login: email,
        wachtwoord: wachtwoord
      }); //gewoon met block en toast

      //gelukt? -- we krijgen een webtoken en data
      await this.afterLogin(res);
    } catch (err) {
      this.log.debug('err bij login', err);
      throw err.code || 'INTERNAL_ERROR'; //throw de string
    }
  }

  /**
   * Vraagt een mail aan met een link om een nieuw wachtwoord in te stellen. Het backend antwoordt altijd
   * hetzelfde, ook als het adres onbekend is: of er een account bij hoort hoort niemand hieruit te kunnen
   * afleiden
   * @param email
   */
  public async wachtwoordVergeten(email: string): Promise<void> {
    await this.send('auth', 'wachtwoordVergeten', {
      scope: 'gebruiker',
      login: email
    });
  }

  /**
   * Vertelt of een resetlink nog werkt. Meer geeft het backend niet terug: met de link alleen hoor je niet
   * te weten bij wie hij hoort
   * @param uid
   * @param code
   */
  public async wachtwoordLinkCheck(uid: string, code: string): Promise<boolean> {
    const res = await this.send('auth', 'wachtwoordLinkCheck', {
      scope: 'gebruiker',
      uid: uid,
      code: code
    }, true, false); //geen errortoast: een dode link is geen storing
    return !!res?.geldig;
  }

  /**
   * Zet een nieuw wachtwoord op grond van een resetlink. Is het e-mailadres meegestuurd en hoort het bij het
   * account, dan zijn we daarna meteen ingelogd. Geeft terug of dat gelukt is
   * @param uid
   * @param code
   * @param wachtwoord
   * @param email Optioneel, om meteen ingelogd te raken
   */
  public async wachtwoordInstellen(uid: string, code: string, wachtwoord: string, email?: string): Promise<boolean> {
    await this.logout();
    try {
      const res = await this.send('auth', 'wachtwoordInstellen', {
        scope: 'gebruiker',
        uid: uid,
        code: code,
        wachtwoord: wachtwoord,
        login: email
      });
      if (!res.ingelogd) {
        return false;
      }
      await this.afterLogin(res);
      return true;
    } catch (err) {
      this.log.debug('err bij wachtwoord instellen', err);
      throw err.code || 'INTERNAL_ERROR'; //throw de string
    }
  }

  /**
   * Geeft de waarde op een usertag terug en/of zet deze. Uit onze userdata-cache, maar synct met backend
   * @param key
   * @param value Als gegeven, dan wordt er een nieuwe waarde gezet. Als null, dan wordt de oude waarde verwijderd
   */
  public async tag(key: string, value?: any): Promise<any> {
    try {
      const oudewaarde = this._userdata.tags[key];
      if (value !== undefined) {
        //okee, wegschrijven
        const data: hash<any> = {key: key};
        data.value = value === null ? null : JSON.stringify(value); //null = wegschrijven
        await this.send('auth', 'tag', data);
        //ook opslaan
        if (value === null) {
          delete this._userdata.tags[key];
        } else {
          this._userdata.tags[key] = data.value; //de json-versie
        }
      }
      this.log.debug(`tag`, key, value, this._userdata.tags);
      return oudewaarde ? JSON.parse(oudewaarde) : oudewaarde;
    } catch (e) {
      this.log.error(e);
      return null;
    }
  }

  /**
   * Laat het backend de betreffende ppi's (per leverancier per week) in bestelbonnen schrijven
   * @param orders
   */
  public async maakInkooporders(orders: inkooporder[]) {
    this.toast(`De inkooporders worden verwerkt`, 'info');
    try {
      let res = await this.send('exact', 'maakinkoop', {orders: orders});
      if (res.mislukt) {
        for (let fout of res.errors) {
          this.toast(fout, 'error', 15000);
        }
        this.toast(`${res.mislukt} regels mislukt. ${res.gelukt} regels gelukt`, 'error', 30000);
      } else {
        this.toast(`De inkooporders zijn ingevoerd in exact. ${res.gelukt} regels verwerkt.`, 'success', 30000);
      }
    } catch (_e) {
      //al wel verwerkt
    }
  }

  /**
   * POC: maak 1 pdf met de betreffende werkbonnen van de ppi's, en toon hem
   * @param personeelplanitems
   */
  public async maakWerkbonPDF(personeelplanitems: number[]) {
    let pdf: any = await this.send('werkbon', 'pdf', {ppi: personeelplanitems});
    if (! pdf?.result) {
      this.toast('Er kon geen pdf gegenereerd worden', 'error', 15000);
      return;
    }
    let pdfblob: Blob = new Blob([new Uint8Array(pdf.result.data ?? pdf.result).buffer], {type: 'application/pdf'});
    let url: string = window.URL.createObjectURL(pdfblob);
    window.open(url);
    //we geven je tien minuten de tijd om de pdf te downloaden, daarna mag de gc aan het werk (als de app er nog is
    setTimeout(() => {
      this.log.debug(`Garbage collect objecturl voor PDF`, url);
      window.URL.revokeObjectURL(url);
    }, 10 * 60 * 1000);
  }

  /**
   * Laat het backend de betreffende ppi's op projecten schrijven als ingediende uren
   * @param ppis
   */
  public async urenIndienen(ppis: number[]) {
    this.toast(`De facturen worden verwerkt`, 'info');
    try {
      let res = await this.send('exact', 'urenindienen', {ppis: ppis});
      if (res.mislukt) {
        for (let fout of res.errors) {
          this.toast(fout, 'error', 15000);
        }
        this.toast(`${res.mislukt} regels mislukt. ${res.gelukt} regels gelukt`, 'error', 30000);
      } else {
        this.toast(`De uren zijn ingediend in exact. ${res.gelukt} regels verwerkt.`, 'success', 30000);
      }
    } catch (_e) {
      //al wel verwerkt
    }
  }

  public async bamImport(project: Project, dienstdata: hash<any>[]) {
    await this.send('specials', 'bamimport', {project: project.id, diensten: dienstdata});
  }

  //////////////////// Toasts -  ook in deze service

  /**
   * Toon een toast, resolvt op hide van de toast
   * @param message
   * @param type
   * @param {-1|0|number} timeOut De automatische timeout. Paar specials: -1 betekent blijft hangen mét close-button, 0 betekent blijft hangen zonder close-button
   * @param title
   */
  public async waittoast(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', timeOut: number = 5000, title: string = ''): Promise<void> {
    const options: Partial<IndividualConfig> = {};
    switch (timeOut) {
      case -1:
        options.disableTimeOut = true;
        options.closeButton = true;
        break;
      case 0:
        options.disableTimeOut = true;
        break;
      default:
        options.timeOut = timeOut;
    }
    const toast = this.toastr[type](message, title, options);
    return firstValueFrom(toast.onHidden); //promise resolvet op hide
  }

  /**
   * Toont een toast zonder te wachten tot hij weer weg is.
   * @see waittoast
   * @param message
   * @param type
   * @param timeOut
   * @param title
   */
  public toast(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', timeOut: number = 5000, title: string = ''): void {
    void this.waittoast(message, type, timeOut, title);
  }


  ///////////////////// Confirm en ok /////////////////////////

  /**
   * Modale confirm met ja/nee knoppen
   * @param titel
   * @param vraag
   */
  public async confirm(titel: string, vraag: string): Promise<boolean> {
    return this.sihwConfirm.confirm(titel, vraag);
  }

  /**
   * Modale msgbox
   * @param titel
   * @param melding
   */
  public async ok(titel: string, melding: string): Promise<void> {
    await this.sihwConfirm.ok(titel, melding);
  }

  ////////////////////////// GENERIEKE DATA-API /////////////////////////////////
  //gebruikt de data-controller in het BE

  /*** generieke getter en setter *****/
  /**
   * Generieke getter met gekoppelde data. Resultaat is een promise die resolvt naar een object (bij single) of array met objecten
   */
  public async records(options: Recordquery, apiblock: boolean = true, errortoast: boolean = true): Promise<Datarecord | Datarecords> {
    try {
      let res = await this.send('data', 'records', options, apiblock, errortoast);
      return res.records;
    } catch (err) {
      throw err.code || 'INTERNAL_ERROR'; //throw de string
    }
  }

  /**
   * Cached record getter
   * @param options De opties voor records(..), zie daar
   * @param tags De tags waaronder gecachede data wordt opgeslagen. Handig ivm cache.cleartags(). Als dit true is, worden de tabelnamen uit de opties gebruikt voor de tags (default). Als dit false is: geen tags
   * @param apiblock Blokkeer de ux tijdens ophalen
   * @param errortoast
   * @param clearBefore Als true, dan wordt de cache niet gebruikt om de data op te halen (dus geforceerd ophalen), maar wordt het resultaat wel gecached
   * @throws - geen eigen catch
   */
  public async cachedRecords(options: Recordquery, tags: string[] | boolean = true, apiblock: boolean = true, errortoast: boolean = true, clearBefore: boolean = false): Promise<Datarecord | Datarecords> {
    try {
      let cachetags: string[];
      if (tags === true) {
        cachetags = ApiService.tagsUitRecordquery(options);
      } else if (tags === false) {
        cachetags = []; //gewoon geen
      } else {
        cachetags = <string[]> tags;
      }
      let res = await this.cachedSend('data', 'records', options, cachetags,apiblock, errortoast, clearBefore);
      return res.records;
    } catch (err) {
      throw err.code || 'INTERNAL_ERROR'; //throw de string
    }
  }


  /**
   * Save één of meer records
   * @param options
   */
  public async save(options: {
    table: string, //de Hoofdtabel
    data: Datarecord | Datarecords, //Het record of meerdere records
    logging?: boolean,
    connected?: //informatie over op te slaan connected data bij 1 record. Bij meerdere zit het in _connected per record in de data
      {
        table: string, //de subtable
        fk?: string, //de foreign key. Standaard <hoofdtable>_id
        fullset?: boolean, //als true, vervang met de meegestuurde data alle gekoppelde data. Missende records dus weg. Is standaard true
        data: hash<any>[] //de data om op te slaan. Kan weer recursief _connected bevatten
      }[]
  }) {
    try {
      let res = await this.send('data', 'save', options);
      this.log.debug(`Saveres`, res);
      return res;
    } catch (err) {
      this.log.error(err);
      throw err.code || 'INTERNAL_ERROR';
    }
  }

  /**
   * Vraag aan het be of een record verwijderd kan worden
   * @param table
   * @param id
   */
  public async canDelete(table: string, id: string | number): Promise<boolean> {
    try {
      let res = await this.send('data', 'deleteRecord', {table: table, id: id, check: true});
      return !!res.result;
    } catch (err) {
      this.log.error(err);
      return false; //dan maar niet
    }
  }

  /**
   * Delete
   * @throws als het niet lukt
   */
  public async delete(table: string, id: string | number, block: boolean = true, errortoast: boolean = true): Promise<void> {
    await this.send('data', 'deleteRecord', {table: table, id: id}, block, errortoast);

  }

  /**
   * Vraag het backend om een record te restoren
   * @param table
   * @param id
   * @param block
   * @param errortoast
   */
  public async restore(table: string, id: string | number, block: boolean = true, errortoast: boolean = true) {
    await this.send('data', 'restoreRecord', {table: table, id: id}, block, errortoast);
  }

  //helper
  public static tagsUitRecordquery(query: Recordquery): string[] {
    let todo = [query]; //agenda
    let q;
    let tags = new Set<string>();
    while ((q = todo.shift())) {
      if (q.table) {
        tags.add(q.table);
        if (q.connected && Array.isArray(q.connected)) {
          todo.push(...q.connected);
        }
      }
    }
    return [...tags];
  }

  ////////////////////////////// PRIVATE ////////////////////////////////////

  /**
   * Sla de teruggekomen logindata op, en toon eventuele meldingen
   * @param data
   * @param onReconnect Geeft aan of we dit doen na een reconnect, we tonen dan geen toast
   * @private
   */
  private async afterLogin(data: {
                             authkey: string,
                             userdata: userdata,
                             reconnectExact?: string,
                             meldingen?: { id: number, titel: string, tekst: string }[]
                           },
                           onReconnect: boolean = false) {
    this.spinJS2.authKey = data.authkey; //altijd een nieuwe
    this._userdata = data.userdata; //hopla
    //we pakken de tags in de userdata pas uit als ze nodig zijn, zie tag(...)
    //sorteer de werkmaatschappijen even op afkorting
    this._userdata.werkmaatschappijen = sortBy(this._userdata.werkmaatschappijen, 'afkorting');
    //set ook onze huidige werkmaatschappij
    this._werkmaatschappij = this._userdata.werkmaatschappijen.find(wm => wm.id === this._userdata.werkmaatschappij);
    if (!onReconnect) {
      this.toast(`Ingelogd als ${data.userdata.gebruikersnaam} Werkmaatschappij: ${this._werkmaatschappij.naam}`, 'success');
    }
    //moeten we exact reconnecten? zet skipexact op true in localconfig om dit nooit te doen
    if (data.reconnectExact && (!this.conf.$('skipexact', false))) {
      //request to reconnectexact
      if (await this.confirm('Exactkoppeling', 'De koppeling met Exact is verlopen. Het is belangrijk dat deze hersteld wordt, zodat de automatische synchronisatie tussen Exact en ATRPlan weer doorloopt. Wil je de koppeling nu herstellen?')) {
        window.open(data.reconnectExact);
      }
    }
    for (let melding of data.meldingen || []) {
      //we doen acties na het sluiten van de toast, maar loopen ze wel meteen allemaal door
      this.waittoast(melding.tekst, 'info', -1, melding.titel).then(() => {
        //terugmelden
        void this.send('auth', 'melding_getoond', {id: melding.id}, false, false); //geen error en geen block
      });
    }
  }

  //////////////////////////////////////////// SEND (met semafoor) ///////////////////////////////////////////////////
  // Throttle/queue-implementatie
  // -------------------------------------------------------------
  // We beperken het aantal gelijktijdige backend-calls naar 1.
  // Als er al een call bezig is, wacht een nieuwe call maximaal 100 ms.
  // Is er binnen die tijd geen slot vrijgekomen, dan mag de call tóch starten
  // (dus tijdelijk >1 tegelijk). Dit dempt de piek zonder de UI te stagneren.
  private sendGelijktijdigMax: number = 1; // maximaal 1 gelijktijdige call
  private sendBezet: number = 0;           // huidig aantal bezette slots
  private sendWachtrij: Array<() => void> = []; // wachtenden die een slot willen
  private readonly sendMaxWachttijdMs: number = 500; // throttle-wachttijd in milliseconden

  /**
   * Vraag een verzendslot aan. Keert terug zodra:
   * - er een slot vrij komt (we claimen het slot), of
   * - de max wachttijd is verstreken (dan gaan we tóch door zonder slot te claimen).
   */
  private async vraagSendSlot(): Promise<void> {
    // Als er ruimte is, claim direct een slot
    if (this.sendBezet < this.sendGelijktijdigMax) {
      this.sendBezet++;
      return;
    }

    // Anders in de wachtrij met timeout
    this.log.debug(`VraagSendSlot: wachten op slot`);
    let vrijgever: (() => void) | null = null;
    const wachtBelofte = new Promise<void>((resolve) => {
      vrijgever = () => {
        this.log.debug(`VraagSendSlot: slot vrijgegeven`);
        resolve();}
      this.sendWachtrij.push(vrijgever!);
    });
    const timeoutBelofte = new Promise<void>((resolve) => setTimeout(resolve, this.sendMaxWachttijdMs));

    await Promise.race([wachtBelofte, timeoutBelofte]);

    // Bepaal of we via de wachtrij of via timeout hier zijn gekomen:
    // - Vrijgave via wachtrij: geefSendSlotVrij() heeft ons al met shift() uit de wachtrij gehaald
    //   en sendBezet vóór het aanroepen van onze vrijgever verhoogd. Onze 'vrijgever' staat dan
    //   NIET meer in de wachtrij (index === -1) → niets meer doen hier.
    // - Timeout: onze 'vrijgever' staat NOG in de wachtrij (index !== -1). In dat geval verwijderen
    //   we hem om dubbele vrijgave te voorkomen en claimen we WÉL een virtueel slot door sendBezet te verhogen,
    //   zodat opvolgende calls maximaal de throttle-wachttijd wachten.
    const index = this.sendWachtrij.indexOf(vrijgever!);
    if (index !== -1) {
      this.log.debug(`VraagSendSlot: slot vrijgegeven wegens timeout`);
      // Timeout-pad: verwijder onze vrijgever uit de wachtrij en claim een virtueel slot
      this.sendWachtrij.splice(index, 1);
      this.sendBezet++;
    }
  }

  /**
   * Geef een bezet slot weer vrij en bedien direct de volgende wachtende, indien aanwezig.
   */
  private geefSendSlotVrij(): void {
    if (this.sendBezet > 0) {
      this.sendBezet--;
    }
    const volgende = this.sendWachtrij.shift();
    if (volgende) {
      // Direct vrijgeven aan de volgende wachtende en slot claimen
      this.sendBezet++;
      volgende();
    }
  }


  public async send(controller: string, action: string, data: any = {}, block: boolean = true, errortoast: boolean = true): Promise<any> {

    this.log.debug(`Send ${controller}.${action}`, data);

    await this.ready;
    //we clonen de data keurig

    data = cloneDeep(data || {});
    //specials die we altijd meesturen tenzij overruled
    let specials = {
      _v: apiversion
    };
    for (let special of Object.keys(specials)) {
      if (!(special in data)) {
        data[special] = specials[special];
      }
    }

    // throttle/queue: wacht op slot vóór we eventueel de UI blokkeren
    await this.vraagSendSlot();
    try {
      if (block) {
        this.block();
      }

      const res = await this.spinJS2.send(controller, action, data);
      this.log.debug(`Receive ${controller}.${action}`, data, res);
      if (block) {
        //setImmediate doet het niet goed in ts
        setTimeout(() => {
          this.unblock();
        }); //heel even wachten. Als er direct een volgende achteraan komt qua loop, dan laten we de blokker even staan
      }
      return res;
    } catch (err) {
      this.log.debug(`Err ${controller}.${action}`, data);
      this.log.debug(err);
      if (block) {
        this.unblock();
      }
      if (err && err.code) {
        if (err.code === 'SPINJS_INVALID_TOKEN') {
          //we gaan uitloggen
          await this.logout();
          window.location.reload(); //FULL STOP
        } else if (errortoast) {
          let msg = beErrors[err.code];
          if (msg) {
            //die is er
            //is er een args? Dan is de eerste om af te beelden
            if (err.args && err.args[0]) {
              msg += ` (${err.args[0]})`;
            }
            this.toast(msg, 'warning');
          }
        }
      }
      //rethrow sowieso
      throw err;
    } finally {
      // slot altijd vrijgeven
      this.geefSendSlotVrij();
    }
  }

  /**
   * Cachedvariant van send. De cachekey is puur controller, action, data. Tags is handig om tabelnamen te noemen - die worden via de datacontroller en datawijziging-api wel weer geleegd wanneer nodig
   * @param controller
   * @param action
   * @param data
   * @param tags
   * @param block
   * @param errortoast
   * @param clearBefore Als true, dan wordt de cache niet gebruikt om de data op te halen (dus geforceerd ophalen), maar wordt het resultaat wel gecached.
   * Eigen implementatie ipv aanroepen send, ivm slot: zo gebruiken we het slot ook als semaphore bij gelijke calls. (Anders zou de volgende call runnen voordat wij de cache hebben bijgewerkt).
   */
  public async cachedSend(controller: string, action: string, data: any = {}, tags: string[] = [], block: boolean = true, errortoast: boolean = true, clearBefore: boolean = false): Promise<any> {
    let blocked: boolean = false;
    const cachekey = {
      c: controller,
      a: action,
      d: data
    };
    await this.ready;
    this.log.debug(`CachedSend ${controller}.${action}`, data);

    // throttle/queue: wacht op slot vóór we eventueel de UI blokkeren of de cache lezen
    await this.vraagSendSlot();

    try {
      let cached: any = (! clearBefore) && this.cache.get(cachekey, tags); //false als we moeten clearen
      if (cached) {
        //altijd asynchroon, zodat code die uitgaat van asynchroon blijft werken
        await new Promise(resolve => {
          setTimeout(resolve)
        });
        this.log.debug(`Cachedsend: gecachet ${controller}.${action}`, data, cached)
        return cached; //finally doet sendslot e.d.
      }

      //anders maar ophalen
      data = cloneDeep(data || {});
      //specials die we altijd meesturen tenzij overruled
      let specials = {
        _v: apiversion
      };
      for (let special of Object.keys(specials)) {
        if (!(special in data)) {
          data[special] = specials[special];
        }
      }
      if (block) {
        this.block();
        blocked = true;
      }
      this.log.debug(`Cachedsend: ophalen ${controller}.${action} van backend`, data);
      let res = await this.spinJS2.send(controller, action, data);  //als het goed gaat, gewoon teruggeven
      this.log.debug(`Cachedsend: receive ${controller}.${action}`, data, res);
      this.cache.add(cachekey, res, tags);
      return res;
    } catch (err) {
      this.log.debug(`Err ${controller}.${action}`, data);
      this.log.debug(err);
      if (err && err.code) {
        if (err.code === 'SPINJS_INVALID_TOKEN') {
          //we gaan uitloggen
          await this.logout();
          window.location.reload(); //FULL STOP
        } else if (errortoast) {
          let msg = beErrors[err.code];
          if (msg) {
            //die is er
            //is er een args? Dan is de eerste om af te beelden
            if (err.args && err.args[0]) {
              msg += ` (${err.args[0]})`;
            }
            this.toast(msg, 'warning');
          }
        }
      }
      //rethrow sowieso
      throw (err);
    } finally {
      //unblock altijd
      if (blocked) {
        this.unblock();
      }
      // slot altijd vrijgeven
      this.geefSendSlotVrij();
    }
  }

  //blocker
  /**
   * Verhoog onze spinner, of begin met blokkeren
   */
  public block(direct: boolean = false) {
    if (this.blockcount <= 0) {
      this.blockcount = 0;
      if (this.blockwait) {
        clearTimeout(this.blockwait);
      }
      if (direct) {
        void this.spinner.show(); //hopla
      } else {
        //we gaan zo blocken, maar even wachten, niet te snel
        this.blockwait = setTimeout(() => {
          this.blockwait = null; //weg
          void this.spinner.show(); //hopla
        }, 300);
      }
    }
    this.blockcount++; //geblockt
  }

  /**
   * Verlaag de blockcount en verwijder eventueel de spinner
   */
  public unblock() {
    this.blockcount--;
    if (this.blockcount <= 0) {
      void this.spinner.hide();
      this.blockcount = 0;
      if (this.blockwait) {
        clearTimeout(this.blockwait);
        this.blockwait = null;
      }
    }
  }

  ///backendnotify
  backendNotify(ev: { msg: string, self?: boolean, data?: any }) {
    this.log.debug(`Backendnotify`, ev);
    switch (ev.msg) {
      case 'datawijziging':
        //data bevat een table en eventueel een keys property
        //keys zijn dan {veldnaam: value} mini-hashes
        //daar kunnen we ook iets mee doen, maar doen we nu niet

        this.log.debug(`Backend meldt een datawijziging`);
        //de tabel is een tag dus weg
        if (ev.data && ev.data.table) {
          //kan ook een array zijn
          this.cache.clearTags(ev.data.table);
          //we stoppen de self-flag in de data
          ev.data.self = !!ev.self;
          //en -daarna- doorsturen naar ons subject
          this.dataWijzigingSubject.next(ev.data);
        } else {
          this.log.warn(`Vreemde backendnotify`, ev);
        }
        break;
      case 'exactImport':
        //nieuwe import vanuit exact
        if (this.can('project', 'full')) {
          this.log.info(`Backend meldt een afgeronde exact-import`);
          this.toast('De gegevens zijn bijgewerkt vanuit Exact.', 'info');
        }
        break;
      case 'exactError':
        //fout bij exact. Alleen afbeelden als exactadmin
        if (this.userdata.exact_admin || this.can('project', 'full')) {
          this.log.warn(`Backend meldt een exact-fout`);
          this.toast(`Exact: ${ev.data.msg}`, 'warning');
        }
        break;
      case 'performance':
        //nieuwe performance data
        this.performancedata = ev.data;
        break;
      case 'registratieVerzoek':
        //backend wil graag dat we ons opnieuw registreren. Dat proberen we, maar alleen als we denken dat we ingelogd zijn
        this.doeHerregistratie();
        break;
      default:
        this.log.warn(`Vreemde backendnotify`, ev);
    }
  }

  /**
   * Handel verzoek van be om herregistratie af. Daar zitten wat voorwaarden aan
   */
  async doeHerregistratie() {
    if (!this.loggedInSync) {
      //we doen alleen een reauth als we (denken dat) we ingelogd zijn
      this.log.debug(`herregistratie: niet ingelogd, dus niets te doen`);
      return;
    }
    this.log.debug(`Verzoek om herregistratie`);
    await this.loggedIn(true, true); //regel het
  }

  /////////////////////////////////////// EXACT ////////////////////////////////////
  async exactUrl() {
    let res = await this.send('exact', 'loginUrl');
    return res.url;
  }

  /**
   * Synchroniseer met exact
   * @param vol Als true, dan alle wm's, anders alleen de huidige. Voor alle wm's is exactadmin nodig
   */
  async syncExact(vol: boolean = false) {
    if (vol && this.userdata?.exact_admin) {
      await this.send('exact', 'sync', {}, false);
    } else {
      await this.send('exact', 'syncwm', {}, false); //be weet welke wm
    }
    this.toast('Synchronisatie met exact ingepland. Dit kan enkele minuten duren.', 'info');
  }
}
